import { FC, useEffect, useMemo } from 'react';
import Typography from '@components/common/typography';
import Input from '@components/common/input';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@hooks/context/useUserContext';
import { IModalComponentProps } from '@context/modal_context';
import { prettifyAmount } from '@utils/index';
import Button from '@components/common/button';
import { Icon } from '@components/icons';
import { useSendAndRequestModals } from '@hooks/modals/useSendAndRequestModals';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAccountService } from '@api';
import { useIsFirstRender } from '@mantine/hooks';
import { useDebounceValue } from 'usehooks-ts';
import { ETransactionAction, ETransactionMethod, ETransactionType } from '@xeppt/xeppt-sdk/types';
import { useFrequency } from '@hooks/helpers/useFrequency';
import { useForm } from '@hooks/helpers/useForm';
import FormField from '@components/form_field';
import { useLocales } from '@hooks/helpers/useLocales';
import { EFundsOperation } from '@enum';
import { FormProvider } from 'react-hook-form';

const LoadCardBalanceModal: FC<IModalComponentProps<{ amount: string }>> = ({
    modalData,
    changeModalData,
    handleChangeDisable
}) => {
    const { t } = useTranslation('modals', {
        keyPrefix: 'load_card_balance'
    });
    const { account, limits } = useUserContext();
    const { handleOpenEstimatedFeesModal } = useSendAndRequestModals();
    const isFirstRender = useIsFirstRender();
    const [amountWithFee, setAmountWithFee] = useDebounceValue(0, 300);
    const [amountWithoutFee, setAmountWithoutFee] = useDebounceValue(0, 300);
    const { amountResultValidation, amountValidation } = useFrequency({
        isFrequencyExist: false,
        method: EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT
    });
    const { validationLocale, placeholderLocale, labelLocale, submitLocale } = useLocales();
    const form = useForm({
        defaultValues: {
            amount: '',
            amount_result: ''
        }
    });

    useEffect(() => {
        if (Object.keys(form.formState.errors).length === 0 || form.formState.isValid) {
            changeModalData({ ...modalData, amount: form.getValues().amount });
        }
        handleChangeDisable(!form.formState.isValid);
    }, [form.formState.errors, form.formState.isValid]);

    const { data: pointsFeeData, handleRequest: handleGetSummary } = useApiQuery({
        method: (data?: { amount: number; fee_included: boolean }) => {
            const values = form.getValues();
            return apiAccountService.calculateLoyaltyFee({
                method: ETransactionMethod.WALLET,
                action: ETransactionAction.CARD_DEPOSIT,
                type: ETransactionType.CREDIT,
                fee_included: data?.fee_included || false,
                amount: Number(data?.fee_included ? values.amount_result : values.amount)
            });
        },
        isInitialRequest: false,
        onSuccess: async (data, request) => {
            if (data.amount > 0 && data.total > 0) {
                const error = amountValidation(data.amount.toString());
                form.setValue(
                    'amount',
                    !request?.fee_included ? data.amount.toFixed(2) : data.amount.toFixed(2)
                );
                form.setValue(
                    'amount_result',
                    request?.fee_included ? data.total.toFixed(2) : data.total.toFixed(2)
                );
                console.log({ error });
                if (error) {
                    form.setError('amount', {
                        message: error
                    });
                    handleChangeDisable(true);
                } else {
                    form.clearErrors('amount');
                    form.clearErrors('amount_result');
                    await form.trigger();
                    handleChangeDisable(false);
                }
            }
        }
    });

    useEffect(() => {
        if (!isFirstRender) {
            handleGetSummary({ amount: amountWithFee, fee_included: true });
        }
    }, [amountWithFee]);

    useEffect(() => {
        if (!isFirstRender) {
            handleGetSummary({ amount: amountWithoutFee, fee_included: false });
        }
    }, [amountWithoutFee]);

    const currentLimits = useMemo(() => {
        return limits?.[EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT];
    }, [limits]);

    const isLimitsExists = useMemo(() => {
        return (
            currentLimits?.per_transfer !== 0 ||
            currentLimits?.daily !== 0 ||
            currentLimits?.weekly !== 0 ||
            currentLimits?.monthly !== 0
        );
    }, []);

    return (
        <FormProvider {...form}>
            <div className={styles.wrapper}>
                <Typography className={styles.title} variant="h4">
                    {t('title')}
                </Typography>
                {isLimitsExists && (
                    <div className={styles.limits_wrapper}>
                        <div className={styles.title}>
                            <Typography variant="body4" weight="semibold">
                                Limits
                            </Typography>{' '}
                        </div>
                        <div className={styles.limits_values}>
                            {currentLimits?.per_transfer !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Maximum amount
                                    </Typography>
                                    <Typography
                                        weight="semibold"
                                        variant="body4"
                                        className={styles.active_amount}>
                                        <b>{(currentLimits?.per_transfer || 0)?.toFixed(2)} CAD</b>
                                    </Typography>
                                </div>
                            )}
                            {currentLimits?.amount_transfer_min !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Minimum amount
                                    </Typography>
                                    <Typography
                                        weight="semibold"
                                        variant="body4"
                                        className={styles.active_amount}>
                                        <b>
                                            {(currentLimits?.amount_transfer_min || 0)?.toFixed(2)}{' '}
                                            CAD
                                        </b>
                                    </Typography>
                                </div>
                            )}
                            {currentLimits?.daily !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Daily
                                    </Typography>
                                    <Typography variant="body4" className={styles.active_amount}>
                                        <b>
                                            {(
                                                (currentLimits?.daily || 0) -
                                                (currentLimits?.daily_used || 0)
                                            )?.toFixed(2)}{' '}
                                            CAD
                                        </b>{' '}
                                        / {(currentLimits?.daily || 0)?.toFixed(2)} CAD
                                    </Typography>
                                </div>
                            )}
                            {currentLimits?.weekly !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Weekly
                                    </Typography>
                                    <Typography variant="body4" className={styles.active_amount}>
                                        <b>
                                            {(
                                                (currentLimits?.weekly || 0) -
                                                (currentLimits?.weekly_used || 0)
                                            )?.toFixed(2)}{' '}
                                            CAD
                                        </b>{' '}
                                        / {(currentLimits?.weekly || 0)?.toFixed(2)} CAD
                                    </Typography>
                                </div>
                            )}
                            {currentLimits?.monthly !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Monthly
                                    </Typography>
                                    <Typography variant="body4" className={styles.active_amount}>
                                        <b>
                                            {(
                                                (currentLimits?.monthly || 0) -
                                                (currentLimits?.monthly_used || 0)
                                            )?.toFixed(2)}{' '}
                                            CAD
                                        </b>{' '}
                                        / {(currentLimits?.monthly || 0)?.toFixed(2)} CAD
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div className={styles.balance_wrapper}>
                    <Typography variant="body3">{t('current_balance')}</Typography>
                    <div className={styles.balance}>
                        <Typography variant="body3">
                            Current XEPPT wallet balance: $
                            <b>{prettifyAmount((account?.balance || 0).toFixed(2) || '')}</b>
                        </Typography>
                    </div>
                </div>
                <div className={styles.inputs_wrapper}>
                    {/*<div className={styles.input_wrapper}>
                        <FormField
                            name="amount_result"
                            renderComponent={({ onChange, value, ...props }) => (
                                <Input
                                    label={labelLocale('total_you_send')}
                                    full
                                    value={value}
                                    onlyNumbers
                                    rightIcon="canadian"
                                    placeholder={placeholderLocale('amount_placeholder')}
                                    fixedAmount
                                    prefix="CAD"
                                    hideErrorIcon
                                    onChange={(val) => {
                                        onChange(val);
                                    }}
                                    onBlur={() => {
                                        if (!form.formState?.errors?.['amount_result']) {
                                            setAmountWithFee(Number(value));
                                        }
                                    }}
                                    {...props}
                                />
                            )}
                            rules={{
                                required: { value: true, message: validationLocale('amount') },
                                validate: amountResultValidation
                            }}
                        />
                    </div>*/}
                    <div className={styles.input_wrapper}>
                        <FormField
                            name="amount"
                            renderComponent={({ onChange, value, ...props }) => (
                                <Input
                                    label={labelLocale('amount_you_add')}
                                    full
                                    value={value}
                                    onlyNumbers
                                    rightIcon="canadian"
                                    placeholder={placeholderLocale('amount_placeholder')}
                                    fixedAmount
                                    prefix="CAD"
                                    hideErrorIcon
                                    onChange={(val) => {
                                        onChange(val);
                                    }}
                                    onBlur={() => {
                                        setAmountWithoutFee(Number(value));
                                    }}
                                    {...props}
                                />
                            )}
                            rules={{
                                required: { value: true, message: validationLocale('amount') },
                                validate: amountValidation
                            }}
                        />
                    </div>
                    <div className={styles.fee_wrapper}>
                        <Typography variant="body4">
                            Includes estimated fees:{' '}
                            <b>{pointsFeeData?.fee?.toFixed(2) || 0} CAD</b>
                        </Typography>
                        <Button
                            leftIcon="info"
                            variant="text-gold"
                            size="small"
                            onClick={() =>
                                handleOpenEstimatedFeesModal({
                                    amount: Number(form.getValues().amount),
                                    method: 'card',
                                    fee: pointsFeeData?.fee || 0
                                })
                            }>
                            {submitLocale('get_info')}
                        </Button>
                    </div>
                    <Typography variant="body3" className={styles.earn_points}>
                        <Icon name="coins" /> On this transaction you will earn{' '}
                        <b>{pointsFeeData?.points.toFixed(2)} points</b>
                    </Typography>
                </div>
            </div>
        </FormProvider>
    );
};

export default LoadCardBalanceModal;
